import { ConfigService, DisciplineService } from "../../services"
import {
  AssociationId,
  FieldConfigKey,
  BaseProfileKey,
  ProfileEventRecord,
  AdhocEventRecordSchema,
  ProcessType,
  CohortName,
} from "../../types"
import { sortArrayOfObjects, toValidPfDate, ensureStringFormat } from "../../utils"
import { AssociationSettings } from "../associationSettings"
import { EmploymentStatus, TrashStatus } from "../common-ts/ProfileDocument"
import { DisciplineRecord } from "../discipline/disciplineRecord"
import PFDoc from "../pfDoc/pfDoc"
import { PpeActionRecordSchema } from "../ppe"

export type ProfilePk = string

export class Profile {
  private pk: ProfilePk = ""
  private uid: string = ""
  private idPassport: string = ""
  private docs: Record<string, string[]> = {}
  private faceShots: Record<string, any>
  private cohorts: string[]
  private trashStatus: TrashStatus = TrashStatus.NONE
  private adHocDocs: Record<string, any>
  private adHocData: Record<string, any>
  private competencies: Record<string, any>
  private employment: any
  private ppe: any
  private discipline: Record<string, any>
  private generalData: Record<string, any>
  private updatedMs: Record<string, number>

  constructor(obj: Record<string, any>, pk?: string) {
    this.pk = pk || obj.idPassport
    this.uid = obj.uid
    this.idPassport = obj.idPassport
    this.docs = obj.docs || {}
    this.faceShots = obj.faceShots || {}
    this.cohorts = obj.cohorts
    this.trashStatus = obj.hasOwnProperty("_obj") ? obj._obj.trashStatus : obj.trashStatus
    this.adHocDocs = obj.adHocDocs || {}
    this.adHocData = obj.adHocData || {}
    this.generalData = obj.generalData || {}
    this.discipline = obj.discipline || {}
    this.employment = obj.employment || {}
    this.competencies = obj.competencies || {}
    this.ppe = obj.ppe || {}
    this.updatedMs = obj.updatedMs || {}
  }

  updateGeneralData(newData: Record<string, any>) {
    for (const [k, v] of Object.entries(newData)) {
      this.generalData[k] = v
    }
  }

  deleteKeys(...keys: string[]): void {
    for (const key of keys) {
      delete (this as any)[key]
    }
  }

  getDisciplineData(selectedAssociation: string, inflateToEventBasedProfile?: boolean): Record<string, any> {
    return this.discipline.getSelectedDisciplineData(selectedAssociation)
  }

  getFaceShot(): any /*FaceShot*/ {
    return this.faceShots
  }

  getPersonalInfo(): { name: string; surname: string; idPassport: string } {
    return {
      name: this.generalData.name,
      surname: this.generalData.surname,
      idPassport: this.generalData.idPassport,
    }
  }

  getGeneralData(): any {
    return this.generalData || {}
  }

  getGeneralDataValue(fieldKey: FieldConfigKey, fallBackValue: any = ""): any {
    const generalData = this.getGeneralData()
    if (generalData.hasOwnProperty(fieldKey)) {
      return generalData[fieldKey]
    } else {
      return fallBackValue
    }
  }

  isEmployeeSuspended(): boolean {
    const generalData = this.getGeneralData()
    const isSuspended = generalData?.isSuspended
    const suspendedValue = ensureStringFormat(isSuspended)
    return suspendedValue === "YES"
  }

  getPk(): ProfilePk {
    return this.pk
  }

  getUid(): string {
    return this.uid
  }

  getIdPassport(): string {
    return this.generalData.idPassport || ""
  }

  getCompetencies(): Record<string, any> {
    return this.competencies
  }

  getFileNameRecord(fileName: string, association: AssociationId) {
    const { adHocDocs, competencies, employment, discipline } = BaseProfileKey
    // TODO: Convert these records to model entities
    if (this.adHocDocs?.[association]) {
      for (const record of this.adHocDocs[association]) {
        if (record.docs.includes(fileName)) {
          const recordType = adHocDocs
          return { record: this.recordNormaliser(record, recordType), recordType }
        }
      }
    }
    if (this.competencies?.history) {
      for (const record of this.competencies?.history) {
        if (record.docs.includes(fileName)) {
          const recordType = competencies
          return { record: this.recordNormaliser(record, recordType), recordType }
        }
      }
    }
    if (this.employment?.history) {
      for (const record of this.employment.history) {
        if (record.docs.includes(fileName)) {
          const recordType = employment
          return { record: this.recordNormaliser(record, recordType), recordType }
        }
      }
    }
    if (this.discipline?.[association]?.disciplineHistory) {
      for (const record of this.discipline[association].disciplineHistory) {
        if (record.docs.includes(fileName)) {
          const recordType = discipline
          return { record: this.recordNormaliser(record, recordType), recordType }
        }
      }
    }
    return null
  }

  recordNormaliser(record: Record<string, any>, recordType: string) {
    let { rawData = {} } = record
    if (recordType === "competencies") {
      if (rawData.hasOwnProperty("competencyIssuedDate") && !rawData.docValidityStartDate) {
        rawData = { ...rawData, docValidityStartDate: toValidPfDate(rawData.competencyIssuedDate || 0) }
      }
      if (rawData.hasOwnProperty("competencyExpiryDate") && !rawData.docValidityExpiryDate) {
        rawData = { ...rawData, docValidityExpiryDate: toValidPfDate(rawData.competencyExpiryDate || 0) }
      }
    }
    if (recordType === "discipline") {
      if (rawData.hasOwnProperty("discActionIssuedDate") && !rawData.docValidityStartDate) {
        rawData = {
          ...rawData,
          docValidityStartDate: toValidPfDate(rawData.discActionIssuedDate || 0),
          discActionIssuedDate: toValidPfDate(rawData.discActionIssuedDate || 0),
        }
      }
      if (rawData.hasOwnProperty("expiry") && !rawData.docValidityExpiryDate) {
        rawData = {
          docValidityExpiryDate: toValidPfDate(record.expiry),
          expiry: toValidPfDate(record.expiry),
        }
      }
      if (rawData.hasOwnProperty("discOffenceDate")) {
        rawData = { discOffenceDate: toValidPfDate(record.discOffenceDate) }
      }
    }
    return { ...record, rawData } as ProfileEventRecord
  }

  getAssociationFileNames(association: AssociationId) {
    if (!this.docs[association]) {
      return []
    }
    return this.docs[association]
  }

  getAllDocs() {
    return this.docs || {}
  }

  getAssociationDocRecords(association: AssociationId) {
    const fileNames = this.getAssociationFileNames(association)
    const results = fileNames.map((fileName) => {
      const mayResult = this.getFileNameRecord(fileName, association)
      if (!mayResult) {
        return
      }
      const { record } = mayResult
      const rawData = record.rawData || {}
      return new PFDoc(association, this.getPk(), fileName, rawData)
    })
    return results.filter((item) => item) as PFDoc[]
  }

  getDocFileNameStrings(selectedAssociation: string = "", startTimestamp?: number, endTimestamp?: number): string[] {
    if (!selectedAssociation) {
      throw { code: "MissingAssociationName" }
    }
    let clientDocs = this.docs[selectedAssociation] || [] // this.clientFileNameMap.get(selectedAssociation) || []
    clientDocs = clientDocs.filter((fileName: string) => {
      const timestamp = parseInt(fileName.split("___")[0])
      let startCriteriaMet = true
      let endCriteriaMet = true
      if (startTimestamp && timestamp < startTimestamp) {
        startCriteriaMet = false
      }
      if (endTimestamp && timestamp > endTimestamp) {
        endCriteriaMet = false
      }
      return startCriteriaMet && endCriteriaMet
    })
    return clientDocs
  }

  getEmploymentStatus() {
    return this.generalData.employmentStatus ?? EmploymentStatus.CANDIDATE
  }

  getOriginalField(key: string): string | string[] | undefined | number {
    return this.generalData.getOriginalField(key)
  }

  getTrashStatus() {
    return this.trashStatus
  }

  getPpe() {
    return this.ppe
  }

  getCohorts(): string[] {
    return this.cohorts || []
  }

  isPartOfCohort(cohortName: string): boolean {
    return this.cohorts?.includes(cohortName)
  }

  unlinkDocuments(selectedAssociation: string, selectedDocuments: string[]): void {
    let docs = this.docs[selectedAssociation] // this.clientFileNameMap.get(selectedAssociation)
    if (docs === undefined) {
      return
    }

    docs = docs.filter((fileName) => !selectedDocuments.includes(fileName))
    this.docs[selectedAssociation] = docs
  }

  getUpdatedMs() {
    return this.updatedMs
  }

  toJson() {
    return {
      pk: this.pk,
      uid: this.uid,
      idPassport: this.idPassport,
      docs: this.docs,
      faceShots: this.faceShots,
      cohorts: this.cohorts,
      trashStatus: this.trashStatus,
      adHocDocs: this.adHocDocs,
      adHocData: this.adHocData,
      generalData: this.generalData,
      discipline: this.discipline,
      competencies: this.competencies,
      ppe: this.ppe,
      updatedMs: this.updatedMs,
    }
  }

  getAllProfileRecordsLabelled(association: AssociationId) {
    function tempRecordNormaliser(record: Record<string, any>) {
      // Needed to account for old records that require migration.
      if (record.rawData) {
        if (!record.rawData.process && record.rawData.action) {
          record.rawData.process = record.rawData.action
        }
      }
      return record
    }

    function addRecordTypeLabel(record: Record<string, any>, value: BaseProfileKey) {
      record.recordType = value
      return tempRecordNormaliser(record)
    }

    const adHocDataRecords = this.getAdHocDataRecords(association).map((record) =>
      addRecordTypeLabel(record, BaseProfileKey.adHocData),
    )
    const adHocDocRecords = this.getAdHocDocRecords(association).map((record) =>
      addRecordTypeLabel(record, BaseProfileKey.adHocDocs),
    )
    const disciplineRecords = this.getDisciplineRecords(association).map((record) =>
      addRecordTypeLabel(record, BaseProfileKey.discipline),
    )
    const employmentRecords = this.getEmploymentRecords().map((record) =>
      addRecordTypeLabel(record, BaseProfileKey.employment),
    )
    const competencyRecords = this.getCompetencyRecords().map((record) =>
      addRecordTypeLabel(record, BaseProfileKey.competencies),
    )
    const ppeRecords = this.getPpeRecords().map((record) => addRecordTypeLabel(record, BaseProfileKey.ppe))
    let allRecords = [
      ...adHocDataRecords,
      ...adHocDocRecords,
      ...disciplineRecords,
      ...employmentRecords,
      ...competencyRecords,
      ...ppeRecords,
    ] as ProfileEventRecord[]

    let processedUids = [] as string[]
    let uniqueRecords = [] as ProfileEventRecord[]
    allRecords.forEach((record) => {
      const recordUid = record.uid || ""
      if (!processedUids.includes(recordUid)) {
        processedUids.push(recordUid)
        uniqueRecords.push(record)
      }
    })
    uniqueRecords = uniqueRecords.map((record) => {
      record.rawData.profilePk = this.pk
      return record
    })
    return sortArrayOfObjects("dateActioned", "descending", uniqueRecords, "rawData") as ProfileEventRecord[]
  }

  getAdHocDataRecords(association: AssociationId) {
    let records = [] as AdhocEventRecordSchema[]
    if (!this.adHocData) {
      return records
    }
    if (!this.adHocData[association]) {
      return records
    }
    const existingDataRecords = this.adHocData[association] || []
    return [...records, ...existingDataRecords]
  }

  getAdHocDocRecords(association: AssociationId) {
    let records = [] as AdhocEventRecordSchema[]
    if (!this.adHocDocs) {
      return records
    }
    if (!this.adHocDocs[association]) {
      return records
    }
    return [...records, ...this.adHocDocs[association]]
  }

  getEmploymentRecords() {
    let records = [] as AdhocEventRecordSchema[]
    if (!this.employment) {
      return records
    }
    if (!this.employment.history) {
      return records
    }
    return [...records, ...this.employment.history]
  }

  getCompetencyRecords() {
    let records = [] as AdhocEventRecordSchema[]
    if (!this.competencies) {
      return records
    }
    if (!this.competencies.history) {
      return records
    }
    return [...records, ...this.competencies.history]
  }

  getDisciplineRecords(association: AssociationId) {
    const terminationRecords = this.getTerminationRecords(association)
    let recordBatch = [...terminationRecords]
    if (!this.discipline) {
      return recordBatch
    }
    const disciplineRecords = this.discipline[association]?.disciplineHistory
    if (!disciplineRecords) {
      return recordBatch
    }
    return [...recordBatch, ...disciplineRecords]
  }

  getDisciplineRecordEntities(association: AssociationId) {
    return this.getDisciplineRecords(association).map((record) => new DisciplineRecord(record))
  }

  getPpeRecords() {
    let records = [] as PpeActionRecordSchema[]
    if (!this.ppe) {
      return records
    }
    if (!this.ppe.history) {
      return records
    }
    return [...records, ...this.ppe.history]
  }

  getTerminationRecords(association: AssociationId) {
    const employmentHistory = this.getEmploymentRecords()
    const terminationRecords = employmentHistory.filter(
      (record) => record.rawData.association === association && record.rawData.process === ProcessType.terminate,
    )
    return terminationRecords
  }

  addAdHocDataRecord = (association: AssociationId, record: AdhocEventRecordSchema) => {
    let records = this.getAdHocDataRecords(association)
    records.unshift(record)
    this.adHocData[association] = records
  }

  getDisciplineFlows = (
    associationEntity: AssociationSettings,
    disciplineService: DisciplineService,
    configService: ConfigService,
    cohort: CohortName,
  ) => {
    try {
      const association = associationEntity.getAssociationSlug()
      const disciplineHistory = this.getDisciplineRecordEntities(association)
      console.log("1. disciplineHistory: ", disciplineHistory)
      const catchAllFlow = disciplineService.generateCatchAllFlow(disciplineHistory)
      const activeCatchAllFlow = catchAllFlow.length ? true : false
      const activeActionFlows = disciplineService.generateActiveActionFlows(
        disciplineHistory,
        activeCatchAllFlow,
        configService,
        associationEntity,
        this.getGeneralData(),
        cohort,
      )
      console.log("1. activeActionFlows: ", activeActionFlows)

      return {
        disciplineHistory,
        activeActionFlows,
        catchAllFlow,
      }
    } catch (error) {
      throw error
    }
  }
}
